import { ButtonSize } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import Skeleton from "@galaxy-io/dls/feedback/Skeleton";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import Flex, { AlignItems, FlexDirection } from "@galaxy-io/dls/layout/Flex";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, navigate } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { AuthLayout } from "@/features/authentication/auth-layout";
import { startHref } from "@/features/authentication/sign-in";

/**
 * `authenticatedAs` is present when somebody is signed in but belongs to no
 * workspace yet — which is every invitee, between signing in and accepting.
 */
interface SessionShape {
  user: { email: string | null } | null;
  authenticatedAs?: string | null;
  authMode?: "dev" | "oidc";
}

interface Invitation {
  email: string;
  workspaceName: string;
  workspaceId: string;
  role?: "admin" | "member";
}

/**
 * Invitation acceptance.
 *
 * Deliberately outside the workspace shell: the invitee has no membership yet,
 * so there is no workspace to render around them. Acceptance is refused unless
 * the verified email matches the invited address — an invitation admits one
 * person, not whoever holds the link.
 */
/** "ada.lovelace@acme.com" → "Ada Lovelace", as a starting point they can edit. */
function nameFrom(email: string): string {
  return (email.split("@")[0] ?? "")
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function InvitationView({ token }: { token: string }) {
  const cache = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");

  /**
   * Who the browser is right now, which decides what this screen can offer.
   * An invitation is reachable without a session, so this is the common case
   * rather than an edge: the invitee usually arrives signed out.
   */
  const session = useQuery({
    queryKey: ["session"],
    retry: false,
    queryFn: async (): Promise<SessionShape> => {
      const response = await fetch("/api/auth/session", { credentials: "include" });
      return (await response.json()) as SessionShape;
    },
  });

  /**
   * Signing in from an invitation.
   *
   * With a provider configured this is a navigation to hosted login, carrying
   * this invitation as the place to return to, so accepting is the next thing
   * the person sees rather than an empty workspace list. The development
   * provider has no hosted screen, so there it stays a request.
   */
  const signIn = useMutation({
    mutationFn: async (email: string) => {
      if (session.data?.authMode !== "dev") {
        window.location.assign(startHref(window.location.pathname, email));
        return;
      }
      const response = await fetch("/api/auth/dev/sign-in", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? "Could not sign in.");
      }
    },
    onSuccess: () => session.refetch(),
    onError: (cause) => setError(cause instanceof Error ? cause.message : "Could not sign in."),
  });

  const invitation = useQuery({
    queryKey: ["invitation", token],
    retry: false,
    queryFn: async (): Promise<Invitation | null> => {
      const response = await fetch(`/api/auth/invitations/${encodeURIComponent(token)}`, {
        credentials: "include",
      });
      if (response.status === 404) return null;
      if (!response.ok) throw new Error("Could not load this invitation.");
      return (await response.json()) as Invitation;
    },
  });

  const accept = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/auth/invitations/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: name.trim() || nameFrom(invitation.data?.email ?? "") }),
      });
      const body = (await response.json().catch(() => null)) as {
        workspaceId?: string;
        error?: string;
      } | null;
      if (!response.ok) throw new Error(body?.error ?? "Could not accept this invitation.");
      return body?.workspaceId ?? "";
    },
    onSuccess: async (workspaceId) => {
      // Accepting is the moment a person stops being a stranger, so the cached
      // session is now wrong in the one way that matters: it still says they
      // belong to nothing, and the workspace would render the sign-in screen
      // to somebody who has just signed in.
      await cache.invalidateQueries({ queryKey: ["session"] });
      navigate(`/w/${workspaceId}`);
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message : "Something went wrong."),
  });

  if (invitation.isPending || session.isPending)
    return (
      <AuthLayout>
        <Skeleton />
      </AuthLayout>
    );
  if (invitation.error || session.error)
    return (
      <AuthLayout>
        <Alert
          variant={AlertVariant.ERROR}
          actions={
            <Button
              label="Retry"
              size={ButtonSize.SMALL}
              onClick={() => {
                void invitation.refetch();
                void session.refetch();
              }}
            />
          }
        >
          {(invitation.error || session.error)?.message}
        </Alert>
      </AuthLayout>
    );

  if (!invitation.data) {
    return (
      <AuthLayout>
        <div>
          <Text as="h1" size={TextSize.HEADING_MD}>
            Invitation unavailable.
          </Text>
          <Text>
            This link has expired, was revoked, or has already been used. Ask your workspace
            administrator for a new one.
          </Text>
        </div>
        <Link href="/">Go to your workspace</Link>
      </AuthLayout>
    );
  }

  const signedInAs = session.data?.user?.email ?? session.data?.authenticatedAs ?? null;
  const matches =
    signedInAs !== null &&
    signedInAs.trim().toLowerCase() === invitation.data.email.trim().toLowerCase();

  return (
    <AuthLayout>
      <div>
        <Text variant={TextVariant.TERTIARY}>You’re invited</Text>
        <Text as="h1" size={TextSize.HEADING_MD}>
          Join {invitation.data.workspaceName}.
        </Text>
        <Text>A shared place for your team’s people and signals.</Text>
      </div>
      <DescriptionList
        items={[
          { label: "Email", value: invitation.data.email },
          { label: "Access", value: invitation.data.role === "admin" ? "Admin" : "Member" },
        ]}
      />
      {error && <Alert variant={AlertVariant.ERROR}>{error}</Alert>}
      {signedInAs === null ? (
        // Signed out, which is how most invitees arrive. Offering "Accept" here
        // would only produce a 401, so the screen signs them in as the address
        // the invitation names — it is not a choice, so it is not a field.
        <Button
          label={`Continue as ${invitation.data.email}`}
          isLoading={signIn.isPending}
          onClick={() => signIn.mutate(invitation.data?.email ?? "")}
        />
      ) : matches ? (
        <Flex alignItems={AlignItems.START} direction={FlexDirection.COLUMN} gap={12}>
          <TextInput
            label="Your name"
            value={name}
            placeholder={nameFrom(invitation.data.email)}
            onChange={(value: string) => setName(value)}
          />
          <Button
            label="Accept invitation"
            isLoading={accept.isPending}
            onClick={() => accept.mutate()}
          />
        </Flex>
      ) : (
        // Signed in as somebody else. An invitation admits one person, so the
        // way forward is to become that person, not to retry as this one.
        <Flex alignItems={AlignItems.START} direction={FlexDirection.COLUMN} gap={12}>
          <Text size={TextSize.CAPTION} variant={TextVariant.TERTIARY}>
            You are signed in as {signedInAs}. This invitation was sent to {invitation.data.email}.
          </Text>
          <Button
            label="Sign in as the invited address"
            isLoading={signIn.isPending}
            onClick={async () => {
              await fetch("/api/auth/sign-out", { method: "POST", credentials: "include" });
              signIn.mutate(invitation.data?.email ?? "");
            }}
          />
        </Flex>
      )}
    </AuthLayout>
  );
}
